import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Icon from '../AppIcon';
import QuickActionBar from './QuickActionBar';
import ConfirmationModal from './ConfirmationModal';
import { logout } from '../../auth/auth';

const PrimaryNavigation = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    {
      id: 'overview',
      label: 'Overview',
      path: '/analytics-overview',
      icon: 'LayoutDashboard',
      description: 'Executive KPIs and system health'
    },
    {
      id: 'agents',
      label: 'Agents',
      path: '/agent-performance',
      icon: 'Bot',
      description: 'AI agent performance monitoring'
    },
    {
      id: 'campaigns',
      label: 'Campaigns',
      path: '/campaigns',
      icon: 'Megaphone',
      description: 'AI-powered outreach campaigns'
    },
    {
      id: 'audience',
      label: 'Audience',
      path: '/audience',
      icon: 'Users',
      description: 'Manage customer audience lists'
    },
    {
      id: 'calls',
      label: 'Calls',
      path: '/call-history-analytics',
      icon: 'Phone',
      description: 'Interaction analysis and tracking'
    },
    {
      id: 'escalations',
      label: 'Escalations',
      path: '/escalations-monitoring',
      icon: 'AlertTriangle',
      description: 'Human handover monitoring'
    },
    {
      id: 'knowledge',
      label: 'Knowledge',
      path: '/knowledge-base-analytics',
      icon: 'BookOpen',
      description: 'Content utilization analytics'
    },
    {
      id: 'nps',
      label: 'NPS',
      path: '/nps-analytics',
      icon: 'Star',
      description: 'Net Promoter Score analytics'
    },
    {
      id: 'order-confirmation',
      label: 'Order Confirm',
      path: '/order-confirmation',
      icon: 'ClipboardCheck',
      description: 'Order confirmation analytics'
    },
    {
      id: 'ndr',
      label: 'NDR',
      path: '/ndr-management',
      icon: 'PackageX',
      description: 'Non-Delivery Report management'
    },
    {
      id: 'shopping-assistance',
      label: 'Shopping Assistance',
      path: '/shopping-assistance',
      icon: 'ShoppingBag',
      description: 'Shopping assistance analytics'
    },
    {
      id: 'integration',
      label: 'Integration',
      path: '/integration',
      icon: 'Plug',
      description: 'Manage integrations and connections'
    },
    {
      id: 'settings',
      label: 'Settings',
      path: '/settings',
      icon: 'Settings',
      description: 'Configure application settings'
    }
  ];

  const isActive = (path) => location?.pathname === path || location?.pathname?.startsWith(path + '/');

  const handleLogoutClick = () => {
    setIsLogoutModalOpen(true);
  };

  const handleLogoutConfirm = () => {
    setIsLogoutModalOpen(false);
    logout();
  };

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const handleNavItemClick = () => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <Link to="/analytics-overview" className="sidebar-logo">
            <div className="sidebar-logo-icon">
              <Icon name="BarChart3" size={24} color="#FFFFFF" />
            </div>
            {!isCollapsed && (
              <span className="sidebar-logo-text">
                Calltify Analytics
              </span>
            )}
          </Link>
          <button
            onClick={toggleSidebar}
            className="sidebar-toggle"
            aria-label="Toggle sidebar"
          >
            <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navigationItems?.map((item) => (
            <Link
              key={item?.id}
              to={item?.path}
              className={`sidebar-nav-item ${isActive(item?.path) ? 'active' : ''}`}
              title={isCollapsed ? item?.label : item?.description}
            >
              <Icon name={item?.icon} size={20} />
              {!isCollapsed && (
                <div className="sidebar-nav-item-content">
                  <span className="sidebar-nav-item-label">{item?.label}</span>
                  <span className="sidebar-nav-item-description">{item?.description}</span>
                </div>
              )}
            </Link>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button
            onClick={handleLogoutClick}
            className="sidebar-logout-btn"
            title={isCollapsed ? 'Logout' : 'Logout from application'}
          >
            <Icon name="LogOut" size={20} />
            {!isCollapsed && <span>Logout</span>}
          </button>
          {!isCollapsed && <QuickActionBar />}
        </div>
      </aside>

      {/* Mobile Top Bar */}
      <div className="mobile-topbar">
        <button
          onClick={handleMobileMenuToggle}
          className="mobile-topbar-toggle"
          aria-label="Toggle mobile menu"
          aria-expanded={isMobileMenuOpen}
        >
          <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
        </button>

        <Link to="/analytics-overview" className="mobile-topbar-logo">
          <div className="sidebar-logo-icon">
            <Icon name="BarChart3" size={24} color="#FFFFFF" />
          </div>
          <span className="sidebar-logo-text">
            Calltify Analytics
          </span>
        </Link>

        <div className="mobile-topbar-actions">
          <QuickActionBar />
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <Link 
            to="/analytics-overview" 
            className="sidebar-logo"
            onClick={handleMobileMenuClose}
          >
            <div className="sidebar-logo-icon">
              <Icon name="BarChart3" size={24} color="#FFFFFF" />
            </div>
            <span className="sidebar-logo-text">
              Calltify Analytics
            </span>
          </Link>

          <button
            onClick={handleMobileMenuClose}
            className="mobile-menu-close"
            aria-label="Close mobile menu"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <nav className="mobile-menu-nav">
          {navigationItems?.map((item) => (
            <Link
              key={item?.id}
              to={item?.path}
              className={`mobile-menu-nav-item ${isActive(item?.path) ? 'active' : ''}`}
              onClick={handleNavItemClick}
            >
              <Icon name={item?.icon} size={20} />
              <div className="mobile-menu-nav-item-content">
                <span className="mobile-menu-nav-item-label">{item?.label}</span>
                <span className="mobile-menu-nav-item-description">{item?.description}</span>
              </div>
            </Link>
          ))}
          
          <button
            onClick={() => {
              handleMobileMenuClose();
              handleLogoutClick();
            }}
            className="mobile-menu-logout-btn"
          >
            <Icon name="LogOut" size={20} />
            <div className="mobile-menu-nav-item-content">
              <span className="mobile-menu-nav-item-label">Logout</span>
              <span className="mobile-menu-nav-item-description">Sign out from application</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogoutConfirm}
        title="Logout"
        description="Are you sure you want to logout? You will need to sign in again to access your account."
        confirmText="Logout"
        cancelText="Cancel"
        iconName="LogOut"
        iconColor="text-primary"
        iconBgColor="bg-primary/10"
      />
    </>
  );
};

export default PrimaryNavigation;